import camelcaseKeys from '@monofront/camelcase-keys';
import {
  IPrismicLinkDestination,
  PrismicLinkContext,
  IPrismicPreview,
  IPrismicCollection,
  FilledLinkField,
} from '@monofront/types';
import { lowercaseFirstLetter } from './string';
import {
  FilledContentRelationshipField,
  ForbiddenError,
  IntegrationField,
  isFilled,
  LinkType,
  NotFoundError,
  ParsingError,
  PreviewTokenExpiredError,
  PrismicError,
  RefExpiredError,
  RefNotFoundError,
  RepositoryNotFoundError,
} from '@prismicio/client';
import { objectify } from 'radash';

export const formatPrismicData = <
  T extends Record<string, unknown> | Array<unknown>,
>(
  data: T,
  stopKeys?: string[],
  exclude?: string[],
): any => {
  const formattedData = camelcaseKeys(data, {
    deep: true,
    stopKeys: [
      'spans',
      'oembed',
      'link',
      'algoliaProducts',
      ...(stopKeys ? stopKeys : []),
    ],
    exclude: [
      'slice_type',
      'slice_label',
      'link_type',
      'first_publication_date',
      'last_publication_date',
      'tag_context',
      ...(exclude ? exclude : []),
    ],
  });

  return formattedData;
};

export const linkResolver = (
  doc: FilledContentRelationshipField,
  context: PrismicLinkContext,
  trackingId?: string,
): string => {
  if (typeof linkResolvers[doc.type] !== 'function') {
    return '';
  }

  const link = linkResolvers[doc.type](context, doc);

  if (trackingId) {
    const params = new URLSearchParams(
      link.includes('?') ? link.split('?')[1] : '',
    );

    params.set('trackingId', trackingId);

    return `${link}?${params.toString()}`;
  }

  return link;
};

const getAbsoluteUrl = (
  context: PrismicLinkContext,
  dest: IPrismicLinkDestination,
  target: string,
) => {
  if (dest === context) {
    return target;
  }

  if (dest === PrismicLinkContext.BLOG) {
    return `https://blog.lafourche.fr${target}`;
  }
  if (dest === 'support') {
    return `https://support.lafourche.fr${target}`;
  }

  return `https://lafourche.fr${target}`;
};

interface ILinkResolvers {
  [key: string]: (
    context: PrismicLinkContext,
    doc: FilledContentRelationshipField,
  ) => string;
}

const linkResolvers: ILinkResolvers = {
  privatelabelhome: (context) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      '/apps/private-label/home',
    ),
  draftproduct: (context, doc: FilledContentRelationshipField) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      `/apps/private-label/draft-product/${doc.uid}`,
    ),
  finalproduct: (context, doc: FilledContentRelationshipField) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      `/apps/private-label/result/${doc.uid}`,
    ),
  shopaccountlogin: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, '/account/login'),
  shopaccountregister: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, '/account/register'),
  shopaccountregisterv2: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, '/account/register'),
  blogpost: (context, doc: FilledContentRelationshipField) =>
    getAbsoluteUrl(context, PrismicLinkContext.BLOG, `/${doc.uid}`),
  blogcategory: (context, doc: FilledContentRelationshipField) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      `https://blog.lafourche.fr/categorie/${doc.uid}`,
    ),
  bloghome: (context) => getAbsoluteUrl(context, PrismicLinkContext.BLOG, '/'),
  shophomepagemember: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, '/home'),
  page: (context, doc: FilledContentRelationshipField) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, `/pages/${doc.uid}`),
  funnel: (context, doc: FilledContentRelationshipField) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, `/account/${doc.uid}`),
  shopcart: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, `/cart`),
  shopwishlist: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, `/pages/my-lists`),
  shoppurchases: (context) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      `/pages/my-lists?tab=my-purchases`,
    ),
  shopaccountaddresses: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, `/account/addresses`),
  shopaccount: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, `/account`),
  shopaccountmanagesubscription: (context) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      `/pages/manage-my-subscription`,
    ),
  shopaccountpaymentmethods: (context) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      `/account/payment-methods`,
    ),
  shopaccountorders: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, `/account/orders`),
  shopaccountinformations: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, `/account/informations`),
  support: (context) => getAbsoluteUrl(context, 'support', `/hc/fr`),
  productbasket: (context, doc: FilledContentRelationshipField) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      `/product-basket/${doc.uid}`,
    ),
  privatelabelresult: (context) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      '/apps/private-label/result',
    ),
  referral: (context) =>
    getAbsoluteUrl(context, PrismicLinkContext.SHOP, '/pages/referral'),
  changesubscription: (context) =>
    getAbsoluteUrl(
      context,
      PrismicLinkContext.SHOP,
      '/pages/manage-my-subscription/change-subscription',
    ),
};

enum DISPLAY_SLICE_FOR {
  ALL = 'all',
  FRESH_USER = 'fresh users',
  NON_FRESH_USER = 'non fresh users',
}

export const canDisplaySliceFor = (
  hasFreshUserTag: boolean,
  displayFor?: string,
): boolean => {
  if (!displayFor) {
    return true; // skip verification if display_for does not exist in the Prismic slice primary contents
  }

  switch (displayFor) {
    case DISPLAY_SLICE_FOR.ALL:
      return true;
    case DISPLAY_SLICE_FOR.FRESH_USER:
      return hasFreshUserTag;
    case DISPLAY_SLICE_FOR.NON_FRESH_USER:
      return !hasFreshUserTag;
    default:
      return true;
  }
};

/**
 * When you create a new tab in Prismic dedicated to A/B testing, you cannot duplicate the keys of the main fields.
 * Therefore, all fields that are related to the document itself (not the slices) must be prefixed.
 * The purpose of this function is to fill all the main keys with the values of the variant
 * in case it is the variant that is to be displayed.
 */
export const formatAbTestDocumentData = (
  documentData: any,
  isVariant: boolean,
) => {
  if (isVariant) {
    return objectify(
      Object.keys(documentData).filter((key) => key.includes('abTest')),
      (key) => lowercaseFirstLetter(key.slice(6)),
      (key) => documentData[key],
    );
  }

  return documentData;
};

export const formatPageContents = (group) =>
  group.reduce((group, { slice }) => {
    const sliceList = slice?.data?.body;

    if (sliceList && sliceList.length) {
      sliceList[0].slice_group_uid = slice.uid;

      return [...group, ...sliceList];
    }

    return group;
  }, []);

export const extractPrismicPreviewUrl = (urlStr: string): IPrismicPreview => {
  const url = new URL(urlStr);
  const token = decodeURIComponent(url.searchParams.get('token') ?? '');
  const documentId = decodeURIComponent(
    url.searchParams.get('documentId') ?? '',
  );

  const tokenUrl = new URL(token);
  const prismicUrl = tokenUrl.host;
  const prismicRepository = prismicUrl.split('.')[0];

  return { token, documentId, prismicUrl, prismicRepository };
};

export const isPrismicErrorRecoverable = (
  error:
    | ParsingError
    | ForbiddenError
    | RepositoryNotFoundError
    | RefNotFoundError
    | PreviewTokenExpiredError
    | NotFoundError
    | RefExpiredError
    | PrismicError<unknown>
    | unknown,
) => {
  return !(
    error instanceof ForbiddenError ||
    error instanceof RepositoryNotFoundError ||
    error instanceof RefNotFoundError ||
    error instanceof PreviewTokenExpiredError ||
    error instanceof NotFoundError ||
    error instanceof RefExpiredError
  );
};

export function isCollectionIntegrationFilled(
  collectionField: IntegrationField,
): collectionField is IntegrationField<IPrismicCollection, 'filled'> {
  return (
    isFilled.integrationField(collectionField) && 'handle' in collectionField
  );
}

export const getKeyFromPrismicLink = (link: FilledLinkField) => {
  if (link.link_type === LinkType.Web) {
    return link.url;
  }

  return link.id;
};
